import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Check, X, Trash2, Mail, Phone, MapPin, CreditCard, Calendar, Smartphone, Globe, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: 'Inscription - en attente de décision',
  payment_pending: 'Paiement soumis - à vérifier',
  approved: 'Approuvé',
  rejected: 'Rejeté'
};

const STATUS_COLORS = {
  approved: 'text-[#4A6B48] bg-[#4A6B48]/10',
  pending: 'text-orange-600 bg-orange-100',
  payment_pending: 'text-blue-600 bg-blue-100',
  rejected: 'text-red-600 bg-red-100'
};

export default function MembersManager() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [decisionModal, setDecisionModal] = useState(null); // { member, status }
  const [decisionMessage, setDecisionMessage] = useState('');

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/members`, { withCredentials: true });
      setMembers(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const openDecisionModal = (member, status) => {
    setDecisionModal({ member, status });
    const defaultMsg = status === 'approved'
      ? `Bonjour ${member.name},\n\nVotre demande d'adhésion à CRESA ASBL a été acceptée. Bienvenue dans notre communauté !\n\nCordialement,\nL'équipe CRESA`
      : `Bonjour ${member.name},\n\nNous avons examiné votre demande d'adhésion. Malheureusement, nous ne pouvons pas y donner suite favorablement à ce stade.\n\nCordialement,\nL'équipe CRESA`;
    setDecisionMessage(defaultMsg);
  };

  const submitDecision = async () => {
    if (!decisionModal) return;
    if (!decisionMessage.trim()) {
      toast.error('Veuillez écrire un message au membre');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/members/${decisionModal.member.member_id}/decision`,
        { status: decisionModal.status, message: decisionMessage },
        { withCredentials: true }
      );
      toast.success(decisionModal.status === 'approved' ? 'Adhésion approuvée' : 'Adhésion rejetée');
      setDecisionModal(null);
      setDecisionMessage('');
      fetchMembers();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleResetToPending = async (memberId) => {
    try {
      await axios.put(`${API}/admin/members/${memberId}/status?status=pending`, {}, { withCredentials: true });
      toast.success('Statut remis en attente');
      fetchMembers();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce membre ?')) return;
    try {
      await axios.delete(`${API}/admin/members/${id}`, { withCredentials: true });
      toast.success('Membre supprimé');
      fetchMembers();
    } catch (error) { toast.error('Erreur'); }
  };

  const filteredMembers = members.filter(m => filter === 'all' || m.status === filter);

  const counts = {
    all: members.length,
    pending: members.filter(m => m.status === 'pending').length,
    payment_pending: members.filter(m => m.status === 'payment_pending').length,
    approved: members.filter(m => m.status === 'approved').length,
    rejected: members.filter(m => m.status === 'rejected').length,
  };

  return (
    <div data-testid="members-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion des Adhérents ({members.length})
        </h2>
      </div>

      <p className="text-sm text-[#5C665A] mb-4 italic">
        Note : la validation d&apos;adhésion peut être prononcée indépendamment du paiement. Vous pouvez accepter
        ou refuser un dossier en envoyant un message personnalisé au candidat.
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'pending', label: 'En attente' },
          { id: 'payment_pending', label: 'Paiement soumis' },
          { id: 'approved', label: 'Approuvés' },
          { id: 'rejected', label: 'Rejetés' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              filter === f.id ? 'bg-[#4A6B48] text-white' : 'bg-white border-2 border-[#E5E3DB] text-[#5C665A]'
            }`}
            data-testid={`member-filter-${f.id}`}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : (
        <div className="space-y-4">
          {filteredMembers.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucun membre dans cette catégorie.</p>
          ) : filteredMembers.map((member) => (
            <div key={member.member_id} className="card" data-testid={`member-item-${member.member_id}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_COLORS[member.status]}`}>
                      {STATUS_LABELS[member.status]}
                    </span>
                    {member.membership_expires_at && (
                      <span className="text-xs text-[#5C665A] flex items-center gap-1">
                        <Calendar size={12} />
                        Expire: {new Date(member.membership_expires_at).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D]">{member.name}</h3>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-[#5C665A]">
                    <div className="flex items-center gap-2"><Mail size={14} />{member.email}</div>
                    <div className="flex items-center gap-2"><Phone size={14} />{member.phone}</div>
                    <div className="flex items-center gap-2"><MapPin size={14} />{member.address}</div>
                    {member.nationality && (
                      <div className="flex items-center gap-2"><Globe size={14} />Nationalité : {member.nationality}</div>
                    )}
                    {member.country_of_residence && (
                      <div className="flex items-center gap-2"><MapPin size={14} />Pays de résidence : {member.country_of_residence}</div>
                    )}
                  </div>
                  <p className="text-sm text-[#5C665A] mt-3 italic">&ldquo;{member.motivation}&rdquo;</p>

                  {member.admin_decision_message && (
                    <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare size={14} className="text-purple-700" />
                        <span className="text-xs font-bold text-purple-900">
                          Message admin envoyé au membre {member.admin_decision_at && `(${new Date(member.admin_decision_at).toLocaleDateString('fr-FR')})`}
                        </span>
                      </div>
                      <p className="text-sm text-[#2E332D] whitespace-pre-wrap">{member.admin_decision_message}</p>
                    </div>
                  )}

                  {member.payment_reference && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="text-blue-600" size={18} />
                        <span className="font-semibold text-blue-900">Preuve de paiement</span>
                      </div>
                      <div className="space-y-1 text-sm text-[#2E332D]">
                        <div><strong>Méthode:</strong> {member.payment_method}</div>
                        <div><strong>Référence:</strong> {member.payment_reference}</div>
                        <div className="flex items-center gap-2">
                          <Smartphone size={14} />
                          <strong>Payeur:</strong> {member.payment_phone}
                        </div>
                        <div><strong>Montant:</strong> ${member.payment_amount} USD</div>
                        {member.payment_submitted_at && (
                          <div className="text-xs text-[#5C665A] mt-2">
                            Soumis le: {new Date(member.payment_submitted_at).toLocaleDateString('fr-FR', { dateStyle: 'long' })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {(member.status === 'pending' || member.status === 'payment_pending') && (
                    <>
                      <button
                        onClick={() => openDecisionModal(member, 'approved')}
                        className="p-2 bg-[#4A6B48]/10 hover:bg-[#4A6B48]/20 rounded text-[#4A6B48]"
                        title="Accepter avec message personnalisé"
                        data-testid={`approve-member-${member.member_id}`}
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => openDecisionModal(member, 'rejected')}
                        className="p-2 bg-red-100 hover:bg-red-200 rounded text-red-600"
                        title="Refuser avec message personnalisé"
                        data-testid={`reject-member-${member.member_id}`}
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {(member.status === 'approved' || member.status === 'rejected') && (
                    <button
                      onClick={() => handleResetToPending(member.member_id)}
                      className="p-2 bg-orange-100 hover:bg-orange-200 rounded text-orange-600"
                      title="Remettre en attente"
                    >
                      <X size={18} />
                    </button>
                  )}
                  <button onClick={() => handleDelete(member.member_id)} className="p-2 hover:bg-gray-100 rounded" title="Supprimer">
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Decision Modal */}
      {decisionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="decision-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {decisionModal.status === 'approved' ? 'Accepter' : 'Refuser'} l&apos;adhésion de {decisionModal.member.name}
            </h3>
            <p className="text-sm text-[#5C665A] mb-4">
              Ce message sera visible par le candidat dans son espace adhérent.
            </p>
            <textarea
              rows={10}
              value={decisionMessage}
              onChange={(e) => setDecisionMessage(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E3DB] rounded-lg text-sm"
              placeholder="Écrivez un message personnalisé..."
              data-testid="decision-message-textarea"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => { setDecisionModal(null); setDecisionMessage(''); }}
                className="px-4 py-2 border border-[#E5E3DB] rounded-lg text-[#5C665A] hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={submitDecision}
                className={`px-4 py-2 rounded-lg text-white font-bold ${
                  decisionModal.status === 'approved' ? 'bg-[#4A6B48] hover:bg-[#3B5539]' : 'bg-red-500 hover:bg-red-600'
                }`}
                data-testid="confirm-decision-button"
              >
                {decisionModal.status === 'approved' ? 'Confirmer l\'acceptation' : 'Confirmer le refus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
